import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Send,
  Paperclip,
  Smile,
  Phone,
  Video,
  MoreVertical,
  Search,
  Users,
  MessageCircle,
  Plus,
  Hash,
  User,
  Image,
  File,
  X,
  Mic,
  Clock,
  Download,
  Play,
  Pause,
  FileText,
  Music,
  VideoIcon,
  Eye,
} from 'lucide-react';
import { toast } from 'react-toastify';

// Type Definitions
interface Participant {
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    profileImage?: string;
    isOnline: boolean;
  };
  role: string;
}

interface Attachment {
  url: string;
  name: string;
  type: string;
  fileType?: string;
  size?: number;
}

interface AvailableUser {
  _id: string;
  firstName: string;
  lastName: string;
  role: string;
  profileImage?: string;
  isOnline?: boolean;
}

interface Conversation {
  _id: string;
  type: 'direct' | 'project' | 'group';
  title?: string;
  participants: Participant[];
  project?: {
    _id: string;
    title: string;
  };
  lastMessage?: {
    content: string;
    createdAt: string;
  };
  lastActivity: string;
}

interface Message {
  _id: string;
  conversation: string;
  content: string;
  sender: {
    _id: string;
    firstName: string;
    lastName: string;
    profileImage?: string;
  };
  type: 'text' | 'file' | 'image' | 'audio' | 'video' | 'document' | 'system';
  attachments: Attachment[];
  readBy: {
    user: string;
    readAt: string;
  }[];
  createdAt: string;
  isEdited: boolean;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};



// Emoji Picker Component
const EmojiPicker: React.FC<{ onEmojiSelect: (emoji: string) => void; onClose: () => void }> = ({ 
  onEmojiSelect, 
  onClose 
}) => {
  const emojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
    '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
    '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
    '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
    '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬',
    '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
    '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯',
    '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐',
    '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈',
    '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉',
    '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '👏',
    '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🦿', '🦵',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
    '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
    '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐',
    '⭐', '🌟', '✨', '⚡', '☄️', '💥', '🔥', '🌈', '☀️', '🌤️'
  ];

  return (
    <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 w-80 max-h-64 overflow-y-auto z-50">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Choose an emoji</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-10 gap-1">
        {emojis.map((emoji, index) => (
          <button
            key={index}
            onClick={() => {
              onEmojiSelect(emoji);
              onClose();
            }}
            className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
};

// File Preview Component
const FilePreview: React.FC<{ attachment: Attachment; onRemove?: () => void; isPreview?: boolean }> = ({ 
  attachment, 
  onRemove, 
  isPreview = false 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const getFileIcon = (type: string, fileType?: string) => {
    if (fileType === 'image' || type.startsWith('image/')) {
      return <Image className="w-4 h-4" />;
    }
    if (fileType === 'audio' || type.startsWith('audio/')) {
      return <Music className="w-4 h-4" />;
    }
    if (fileType === 'video' || type.startsWith('video/')) {
      return <VideoIcon className="w-4 h-4" />;
    }
    if (fileType === 'document' || type === 'application/pdf' || type.includes('document')) {
      return <FileText className="w-4 h-4" />;
    }
    return <File className="w-4 h-4" />;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleAudioToggle = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const renderAttachmentContent = () => {
    const fileType = attachment.fileType || (attachment.type.startsWith('image/') ? 'image' : 
                     attachment.type.startsWith('audio/') ? 'audio' :
                     attachment.type.startsWith('video/') ? 'video' : 'file');

    switch (fileType) {
      case 'image':
        return (
          <div className="relative group">
            <img
              src={attachment.url}
              alt={attachment.name}
              className="max-w-xs max-h-48 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setShowImageModal(true)}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {showImageModal && (
              <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setShowImageModal(false)}>
                <div className="relative max-w-4xl max-h-4xl p-4">
                  <img src={attachment.url} alt={attachment.name} className="max-w-full max-h-full object-contain" />
                  <button
                    onClick={() => setShowImageModal(false)}
                    className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="flex items-center space-x-3 bg-gray-100 dark:bg-gray-700 rounded-lg p-3 max-w-xs">
            <button
              onClick={handleAudioToggle}
              className="flex-shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {attachment.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatFileSize(attachment.size)}
              </p>
            </div>
            <audio
              ref={audioRef}
              src={attachment.url}
              onEnded={() => setIsPlaying(false)}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
          </div>
        );

      case 'video':
        return (
          <div className="max-w-xs">
            <video
              src={attachment.url}
              controls
              className="w-full rounded-lg"
              style={{ maxHeight: '200px' }}
            >
              Your browser does not support the video tag.
            </video>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
              {attachment.name}
            </p>
          </div>
        );

      default:
        return (
          <div className="flex items-center space-x-3 bg-gray-100 dark:bg-gray-700 rounded-lg p-3 max-w-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-lg flex items-center justify-center">
              {getFileIcon(attachment.type, attachment.fileType)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {attachment.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatFileSize(attachment.size)}
              </p>
            </div>
            <a
              href={attachment.url}
              download={attachment.name}
              className="flex-shrink-0 text-blue-500 hover:text-blue-600"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="w-4 h-4" />
            </a>
          </div>
        );
    }
  };

  return (
    <div className="relative">
      {renderAttachmentContent()}
      {onRemove && isPreview && (
        <button
          onClick={onRemove}
          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

const ChatInterface: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    socket,
    isConnected,
    sendMessage: socketSendMessage,
    joinConversation,
    leaveConversation,
    startTyping,
    stopTyping,
    typingUsers,
    onlineUsers,
  } = useSocket();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [showAttachmentOptions, setShowAttachmentOptions] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showNewConversationModal, setShowNewConversationModal] = useState<boolean>(false);
  const [newConversationUsers, setNewConversationUsers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string>('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const API_BASE_URL = getApiBaseUrl();

  const fetchConversations = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/conversations`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch conversations');
      }

      const data = await response.json();
      const filteredConversations =
        user?.role === 'admin'
          ? data.conversations.filter((c: Conversation) =>
              c.participants.some((p: Participant) => p.user._id === user._id)
            )
          : data.conversations;

      setConversations(filteredConversations || []);
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (id: string): Promise<void> => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/conversations/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch conversation');
      }

      const data = await response.json();
      if (data.conversation) {
        if (
          user?.role !== 'admin' ||
          data.conversation.participants.some(
            (p: Participant) => p.user._id === user._id
          )
        ) {
          setConversations((prev) => {
            const exists = prev.some((c) => c._id === data.conversation._id);
            if (exists) {
              return prev.map((c) =>
                c._id === data.conversation._id ? data.conversation : c
              );
            } else {
              return [...prev, data.conversation];
            }
          });
          setActiveConversation(data.conversation);
          fetchMessages(data.conversation._id);
          joinConversation(data.conversation._id);
        }
      }
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  const fetchMessages = async (convoId: string): Promise<void> => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_BASE_URL}/conversations/${convoId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      setMessages(data.messages || []);
      markMessagesAsRead(convoId);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const markMessagesAsRead = async (convoId: string): Promise<void> => {
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${API_BASE_URL}/conversations/${convoId}/read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/conversations/available-users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await res.json();
      setAvailableUsers(data.users);
    } catch (error) {
      toast.error('Could not load users');
    }
  };

  const createNewConversation = async (): Promise<void> => {
    if (newConversationUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const participants =
        user?.role === 'admin'
          ? [...newConversationUsers, user._id]
          : newConversationUsers;

      const response = await fetch(`${API_BASE_URL}/conversations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: newConversationUsers.length === 1 ? 'direct' : 'group',
          participants,
          title: newConversationUsers.length > 1 ? 'Group Chat' : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create conversation');
      }

      const data = await response.json();

      setConversations((prev) => {
        const exists = prev.some((c) => c._id === data.conversation._id);
        if (exists) {
          return prev.map((c) =>
            c._id === data.conversation._id ? data.conversation : c
          );
        } else {
          return [...prev, data.conversation];
        }
      });

      setActiveConversation(data.conversation);
      navigate(`/dashboard/chat/${data.conversation._id}`, { replace: true });
      joinConversation(data.conversation._id);
      setNewConversationUsers([]);
      setShowNewConversationModal(false);
      toast.success('Conversation created successfully');
    } catch (error) {
      toast.error('Failed to create conversation');
    }
  };

  const handleSendMessage = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if ((!newMessage.trim() && attachments.length === 0) || !activeConversation)
      return;

    if (
      user?.role === 'admin' &&
      !activeConversation.participants.some(
        (p: Participant) => p.user._id === user._id
      )
    ) {
      toast.error('You are no longer a participant in this conversation');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('content', newMessage);
      formData.append('type', 'text');

      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch(
        `${API_BASE_URL}/conversations/${activeConversation._id}/messages`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      
      // Clear input immediately for better UX
      const messageContent = newMessage;
      const messageAttachments = [...attachments];
      setNewMessage('');
      setAttachments([]);
      setShowAttachmentOptions(false);
      setShowEmojiPicker(false);

      // Stop typing indicator
      if (isTyping) {
        stopTyping(activeConversation._id);
        setIsTyping(false);
      }

      // Update conversation list
      setConversations((prev) =>
        prev.map((c) =>
          c._id === activeConversation._id
            ? {
                ...c,
                lastMessage: {
                  content: data.message.content || `Sent ${data.message.attachments?.length || 0} file(s)`,
                  createdAt: data.message.createdAt,
                },
                lastActivity: data.message.createdAt,
              }
            : c
        )
      );

      // The message will be added to the UI via socket event
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...newFiles]);
      setShowAttachmentOptions(false);
    }
  };

  const removeAttachment = (index: number): void => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTyping = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setNewMessage(e.target.value);

    if (activeConversation) {
      if (e.target.value.length > 0 && !isTyping) {
        setIsTyping(true);
        startTyping(activeConversation._id);
      } else if (e.target.value.length === 0 && isTyping) {
        setIsTyping(false);
        stopTyping(activeConversation._id);
      }

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set new timeout to stop typing after 2 seconds of inactivity
      if (e.target.value.length > 0) {
        typingTimeoutRef.current = setTimeout(() => {
          if (isTyping) {
            setIsTyping(false);
            stopTyping(activeConversation._id);
          }
        }, 2000);
      }
    }
  };

  const handleEmojiSelect = (emoji: string): void => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getConversationTitle = (conversation: Conversation): string => {
    if (conversation.title) return conversation.title;
    if (conversation.type === 'project' && conversation.project) {
      return conversation.project.title;
    }
    if (conversation.type === 'direct') {
      const otherParticipant = conversation.participants.find(
        (p: Participant) => p.user._id !== user?._id
      );
      return otherParticipant
        ? `${otherParticipant.user.firstName} ${otherParticipant.user.lastName}`
        : 'Direct Message';
    }
    return 'Group Chat';
  };

  const getConversationIcon = (conversation: Conversation): JSX.Element => {
    switch (conversation.type) {
      case 'project':
        return <Hash className="w-4 h-4" />;
      case 'group':
        return <Users className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  const getTypingIndicator = (conversationId: string): string => {
    const typing = typingUsers[conversationId] || [];
    const typingNames = typing
      .filter(userId => userId !== user?._id)
      .map(userId => {
        const participant = activeConversation?.participants.find(p => p.user._id === userId);
        return participant ? participant.user.firstName : 'Someone';
      });

    if (typingNames.length === 0) return '';
    if (typingNames.length === 1) return `${typingNames[0]} is typing...`;
    if (typingNames.length === 2) return `${typingNames[0]} and ${typingNames[1]} are typing...`;
    return `${typingNames[0]} and ${typingNames.length - 1} others are typing...`;
  };

  const filteredConversations = conversations.filter(
    (conversation: Conversation) =>
      getConversationTitle(conversation)
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  // Socket event listeners
  useEffect(() => {
    if (socket) {
      const handleNewMessage = (message: Message) => {
        if (message.conversation === activeConversation?._id) {
          setMessages((prev) => {
            const exists = prev.some((m) => m._id === message._id);
            if (exists) {
              return prev;
            }
            return [...prev, message];
          });

          // Update conversation in sidebar
          setConversations((prev) =>
            prev.map((c) =>
              c._id === message.conversation
                ? {
                    ...c,
                    lastMessage: {
                      content: message.content || `Sent ${message.attachments?.length || 0} file(s)`,
                      createdAt: message.createdAt,
                    },
                    lastActivity: message.createdAt,
                  }
                : c
            )
          );
        } else {
          // Update conversation in sidebar even if not active
          setConversations((prev) =>
            prev.map((c) =>
              c._id === message.conversation
                ? {
                    ...c,
                    lastMessage: {
                      content: message.content || `Sent ${message.attachments?.length || 0} file(s)`,
                      createdAt: message.createdAt,
                    },
                    lastActivity: message.createdAt,
                  }
                : c
            )
          );
        }
      };

      const handleMessageUpdated = (message: Message) => {
        if (message.conversation === activeConversation?._id) {
          setMessages((prev) =>
            prev.map((m) => (m._id === message._id ? message : m))
          );
        }
      };

      socket.on('new_message', handleNewMessage);
      socket.on('message_updated', handleMessageUpdated);

      return () => {
        socket.off('new_message', handleNewMessage);
        socket.off('message_updated', handleMessageUpdated);
      };
    }
  }, [socket, activeConversation?._id]);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (conversationId) {
      const convo = conversations.find((c) => c._id === conversationId);
      if (convo) {
        setActiveConversation(convo);
        fetchMessages(conversationId);
        joinConversation(conversationId);
      } else if (conversations.length > 0) {
        fetchConversation(conversationId);
      }
    } else if (conversations.length > 0) {
      const firstValidConvo =
        user?.role === 'admin'
          ? conversations.find((c) =>
              c.participants.some((p: Participant) => p.user._id === user._id)
            )
          : conversations[0];

      if (firstValidConvo) {
        setActiveConversation(firstValidConvo);
        fetchMessages(firstValidConvo._id);
        joinConversation(firstValidConvo._id);
        navigate(`/dashboard/chat/${firstValidConvo._id}`, { replace: true });
      }
    }

    return () => {
      if (conversationId) {
        leaveConversation(conversationId);
      }
    };
  }, [conversationId, conversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchAvailableUsers();
  }, []);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-full">
        <div className="w-80 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="p-4 space-y-4">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-3 rounded-lg animate-pulse"
              >
                <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading conversations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-white dark:bg-gray-900">
      {/* Sidebar */}
      <div className="w-80 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Messages
            </h2>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} title={isConnected ? 'Connected' : 'Disconnected'} />
              <button
                onClick={() => setShowNewConversationModal(true)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No conversations found</p>
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <div
                key={conversation._id}
                onClick={() => {
                  setActiveConversation(conversation);
                  navigate(`/dashboard/chat/${conversation._id}`);
                }}
                className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  activeConversation?._id === conversation._id
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-r-blue-500'
                    : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    {conversation.type === 'direct' ? (
                      <div className="relative">
                        {conversation.participants.find(
                          (p) => p.user._id !== user?._id
                        )?.user.profileImage ? (
                          <img
                            src={
                              conversation.participants.find(
                                (p) => p.user._id !== user?._id
                              )?.user.profileImage
                            }
                            alt="Profile"
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          </div>
                        )}
                        {conversation.participants.find(
                          (p) => p.user._id !== user?._id && onlineUsers.includes(p.user._id)
                        ) && (
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
                        )}
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        {getConversationIcon(conversation)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {getConversationTitle(conversation)}
                      </h3>
                      {conversation.lastMessage && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {getRelativeTime(conversation.lastMessage.createdAt)}
                        </span>
                      )}
                    </div>
                    {conversation.lastMessage && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {conversation.lastMessage.content}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {activeConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    {activeConversation.type === 'direct' ? (
                      <div className="relative">
                        {activeConversation.participants.find(
                          (p) => p.user._id !== user?._id
                        )?.user.profileImage ? (
                          <img
                            src={
                              activeConversation.participants.find(
                                (p) => p.user._id !== user?._id
                              )?.user.profileImage
                            }
                            alt="Profile"
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </div>
                        )}
                        {activeConversation.participants.find(
                          (p) => p.user._id !== user?._id && onlineUsers.includes(p.user._id)
                        ) && (
                          <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-green-500 border border-white dark:border-gray-800 rounded-full"></div>
                        )}
                      </div>
                    ) : (
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        {getConversationIcon(activeConversation)}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      {getConversationTitle(activeConversation)}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {activeConversation.participants.length} participants
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    <Phone className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    <Video className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message._id}
                  className={`flex ${
                    message.sender._id === user?._id ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md ${
                      message.sender._id === user?._id
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                    } rounded-lg p-3`}
                  >
                    {message.sender._id !== user?._id && (
                      <p className="text-xs font-medium mb-1 opacity-75">
                        {message.sender.firstName} {message.sender.lastName}
                      </p>
                    )}
                    
                    {message.content && (
                      <p className="text-sm">{message.content}</p>
                    )}
                    
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {message.attachments.map((attachment, index) => (
                          <FilePreview
                            key={index}
                            attachment={attachment}
                          />
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs opacity-75">
                        {new Date(message.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      {message.isEdited && (
                        <span className="text-xs opacity-75">edited</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Typing Indicator */}
              {activeConversation && getTypingIndicator(activeConversation._id) && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 max-w-xs">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {getTypingIndicator(activeConversation._id)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Attachment Preview */}
            {attachments.length > 0 && (
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex flex-wrap gap-2">
                  {attachments.map((file, index) => (
                    <div key={index} className="relative">
                      <FilePreview
                        attachment={{
                          url: URL.createObjectURL(file),
                          name: file.name,
                          type: file.type,
                          size: file.size,
                        }}
                        onRemove={() => removeAttachment(index)}
                        isPreview={true}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <form onSubmit={handleSendMessage} className="flex items-end space-x-2">
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={handleTyping}
                      placeholder="Type a message..."
                      className="w-full px-4 py-2 pr-20 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowAttachmentOptions(!showAttachmentOptions)}
                          className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                        >
                          <Paperclip className="w-4 h-4" />
                        </button>
                        
                        {showAttachmentOptions && (
                          <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 min-w-[120px]">
                            <button
                              type="button"
                              onClick={() => {
                                fileInputRef.current?.click();
                                setShowAttachmentOptions(false);
                              }}
                              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            >
                              <File className="w-4 h-4" />
                              <span>File</span>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                        >
                          <Smile className="w-4 h-4" />
                        </button>
                        
                        {showEmojiPicker && (
                          <EmojiPicker
                            onEmojiSelect={handleEmojiSelect}
                            onClose={() => setShowEmojiPicker(false)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={!newMessage.trim() && attachments.length === 0}
                  className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt"
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Select a conversation
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Choose a conversation from the sidebar to start messaging
              </p>
            </div>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                New Conversation
              </h3>
              <button
                onClick={() => setShowNewConversationModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Users
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg">
                  {availableUsers.map((availableUser) => (
                    <label
                      key={availableUser._id}
                      className="flex items-center space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={newConversationUsers.includes(availableUser._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewConversationUsers((prev) => [...prev, availableUser._id]);
                          } else {
                            setNewConversationUsers((prev) =>
                              prev.filter((id) => id !== availableUser._id)
                            );
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex items-center space-x-2">
                        {availableUser.profileImage ? (
                          <img
                            src={availableUser.profileImage}
                            alt="Profile"
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {availableUser.firstName} {availableUser.lastName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {availableUser.role}
                          </p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowNewConversationModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={createNewConversation}
                  disabled={newConversationUsers.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;

